import ClippedTooltip from 'components/ClippedTooltip/ClippedTooltip';
import Dropdown from 'components/Dropdown/Dropdown';
import NumberInput from 'components/NumberInput';
import { AdditionalProductGrouped, OptionType, UnitPriceData } from 'lib/types';
import React, { useEffect, useState } from 'react';
import './AdditionalAssortmentRow.scss';
import ProductLinker from 'components/ProductLinker';
import { useDevice } from 'lib/hooks/useDevice';

interface AdditionalAssortmentRowProps {
    product: AdditionalProductGrouped;
    calculateUnitPrice: (value: number, discount: number, converterM2: number) => number;
    calculateTotalPrice: (count: number, price: number, discount: number, converterM2: number, dimensionDefault: number, isDimension: boolean) => number;
    assortmentQuantity: Array<number>;
    setAdditionalAssortmentQuantity: (quantity: number[]) => void;
    onClick: () => void;
}

const AdditionalAssortmentRow = ({ product, calculateUnitPrice, calculateTotalPrice, assortmentQuantity, setAdditionalAssortmentQuantity, onClick }: AdditionalAssortmentRowProps) => {
    const { isMedium } = useDevice();
    const [selectedUnit, setSelectedUnit] = useState<UnitPriceData | undefined>(product.units.length === 1 ? product.units[0] : undefined);
    const [defaultUnit, setDefaultUnit] = useState<UnitPriceData>(product.units.find((u) => u.isDefault === true) ?? product.units[0]);
    useEffect(() => {
        setSelectedUnit(product.units.length === 1 ? product.units[0] : undefined);
        setDefaultUnit(product.units.find((u) => u.isDefault === true) ?? product.units[0]);
    }, [product]);
    return isMedium ?
        (
            <div className='AdditionalAssortmentRow-product-list-item'>
                <ProductLinker onClick={onClick} unit={selectedUnit?.unit ?? product.units[0].unit} symKar={product.symKar} className='AdditionalAssortmentRow-description-wrapper'>
                    <ClippedTooltip text={product.description} className={'AdditionalAssortmentRow-hyper'} />
                </ProductLinker>
                <div className='AdditionalAssortmentRow-button-wrapper'>
                    <div className='AdditionalAssortmentRow-dropdown'>
                        <Dropdown
                            placeholder={product.units.length === 1 ? product.units[0].unit : 'Wybierz wolumen'}
                            isClearable={false}
                            isDisabled={product.units.length === 1}
                            options={product.units.map((unit) => ({ label: unit.unit, value: unit }))}
                            value={null}
                            isSearchable={false}
                            onChange={(option) => { if (!option) return; setSelectedUnit((option as OptionType<UnitPriceData>).value); }}
                        />
                    </div>
                    <div className='AdditionalAssortmentRow-price-wrapper'>
                        <div className='price-color' style={{ fontSize: '1rem' }}>
                            {`${calculateUnitPrice(defaultUnit.price, defaultUnit.discount, defaultUnit.converterM2).toFixed(2)} /`}
                            {(defaultUnit.converterM2 === 0 || defaultUnit.unit !== 'mb') ? defaultUnit.unit : <>m<sup>2</sup></>}
                        </div>
                        {defaultUnit.isDimension &&
                            <div className='dimension' style={{ fontSize: '0.8rem' }}>
                                {`Wymiar: ${(defaultUnit.converterM2 === 0 || defaultUnit.unit !== 'mb') ? defaultUnit.dimensionDefault.toFixed(3) : (defaultUnit.converterM2 * defaultUnit.dimensionDefault).toFixed(3)}`}
                            </div>}
                    </div>

                    <NumberInput
                        value={selectedUnit ? assortmentQuantity[selectedUnit.productIndex] : 0}
                        setValue={(value: number) => {
                            if (!selectedUnit) return;
                            const newQuantity = [...assortmentQuantity];
                            newQuantity[selectedUnit.productIndex] = !Number.isNaN(value) ? Math.abs(value) : 1;
                            setAdditionalAssortmentQuantity(newQuantity);
                        }}
                        disabledEdit={selectedUnit ? assortmentQuantity[selectedUnit.productIndex] === 0 : true}
                        min={0}
                        max={100000}
                    />
                    <div className='AdditionalAssortmentRow-summary-price-wrapper'>
                        {selectedUnit ? `${calculateTotalPrice(assortmentQuantity[selectedUnit.productIndex], selectedUnit.price, selectedUnit.discount, selectedUnit.converterM2, selectedUnit.dimensionDefault, selectedUnit.isDimension).toFixed(2)}` : (0).toFixed(2)}
                    </div>
                </div>
            </div>
        ) :
        (
            <div className='AdditionalAssortmentRow-product-list-card'>
                <ProductLinker onClick={onClick} unit={selectedUnit?.unit ?? product.units[0].unit} symKar={product.symKar} className='AdditionalAssortmentRow-card-header'>
                    <div className='AdditionalAssortmentRow-hyper'>
                        {product.description}
                    </div>
                </ProductLinker>
                <div className='AdditionalAssortmentRow-product-card'>
                    <div className='AdditionalAssortmentRow-price-wrapper'>
                        <div className='price-color' style={{ fontSize: '2rem' }}>
                            {`${calculateUnitPrice(defaultUnit.price, defaultUnit.discount, defaultUnit.converterM2).toFixed(2)} /`}
                            {(defaultUnit.converterM2 === 0 || defaultUnit.unit !== 'mb') ? defaultUnit.unit : <>m<sup>2</sup></>}
                        </div>
                        {defaultUnit.isDimension &&
                            <div className='dimension' style={{ fontSize: '1rem' }}>
                                {`Wymiar: ${(defaultUnit.converterM2 === 0 || defaultUnit.unit !== 'mb') ? defaultUnit.dimensionDefault.toFixed(3) : (defaultUnit.converterM2 * defaultUnit.dimensionDefault).toFixed(3)}`}
                            </div>}
                    </div>
                    <div className='AdditionalAssortmentRow-dropdown'>
                        <Dropdown
                            placeholder={product.units.length === 1 ? product.units[0].unit : 'Wybierz wolumen'}
                            isClearable={false}
                            isDisabled={product.units.length === 1}
                            options={product.units.map((unit) => ({ label: unit.unit, value: unit }))}
                            value={null}
                            isSearchable={false}
                            onChange={(option) => { if (!option) return; setSelectedUnit((option as OptionType<UnitPriceData>).value); }}
                        />
                    </div>
                    <div className='AdditionalAssortmentRow-amount-wrapper'>
                        <NumberInput
                            value={selectedUnit ? assortmentQuantity[selectedUnit.productIndex] : 0}
                            setValue={(value: number) => {
                                if (!selectedUnit) return;
                                const newQuantity = [...assortmentQuantity];
                                newQuantity[selectedUnit.productIndex] = !Number.isNaN(value) ? Math.abs(value) : 1;
                                setAdditionalAssortmentQuantity(newQuantity);
                            }}
                            disabledEdit={selectedUnit ? assortmentQuantity[selectedUnit.productIndex] === 0 : true}
                            min={0}
                            max={100000}
                        />
                        <div className='AdditionalAssortmentRow-summary-price-wrapper'>
                            {selectedUnit ? `${calculateTotalPrice(assortmentQuantity[selectedUnit.productIndex], selectedUnit.price, selectedUnit.discount, selectedUnit.converterM2, selectedUnit.dimensionDefault, selectedUnit.isDimension).toFixed(2)}` : (0).toFixed(2)}
                        </div>
                    </div>
                </div>
            </div>
        );
};

export default AdditionalAssortmentRow;
